import React from "react";

import {
  CalendarOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion/dist/framer-motion";
import Highlighter from "react-highlight-words";

import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import { submissionHasRetestSample } from "../helper";
import ActionsMenu from "./ActionsMenu";
import SampleIssuePill from "./SampleIssuePill";
import {
  getSubmissionExpiryDisplay,
  getTrackingInfoTooltipRows,
  hasSubmissionExpiry,
  hasSubmissionTrackingInfo,
  TRACKING_INFO_TOOLTIP,
} from "./submissionExpiryUtils";
import {
  ALL_SAMPLES_HAVE_ISSUES_TOOLTIP,
  allSamplesHaveIssues,
  getSampleIssueColumnDisplay,
  hasSampleIssues,
  isExpandableSampleIssues,
} from "./submissionIssueUtils";
import {
  getScheduledSubmissionTooltip,
  getSubmissionStatusClassName,
  getSubmissionStatusLabel,
  isDeletedSubmission,
  isScheduledSubmission,
} from "./submissionStatusUtils";
import {
  formatSubmissionDateTime,
  getActivityHistoryItems,
  getTotalTestsDisplay,
} from "./submissionTableUtils";
import SubmissionTypeLabel from "./SubmissionTypeLabel";

import { PRODUCT_ENVIRONMENT_CATEGORY } from "../../utils";

import { ReactComponent as ProductEnvironmentIcon } from "../../../../assets/icons/Product-Environment.svg";
import { ReactComponent as ChevronRight } from "../../../../assets/images/common/ChevronDown.svg";
import { ReactComponent as EnvironmentIcon } from "../../../../assets/images/feature-icons/EnvironmentIconFilled.svg";
import { ReactComponent as ProductIcon } from "../../../../assets/images/feature-icons/ProductIconFilled.svg";
import { ReactComponent as ActionTaken } from "../../../../assets/images/sample-submission/ActionTaken.svg";
import { ReactComponent as DeleteByIcon } from "../../../../assets/images/sample-submission/DeleteByIcon.svg";
import { ReactComponent as ExpiryFlag } from "../../../../assets/images/sample-submission/ExpiryFlag.svg";
import { ReactComponent as ExpiryTimer } from "../../../../assets/images/sample-submission/ExpiryTimer.svg";
import { ReactComponent as ScheduledIcon } from "../../../../assets/images/sample-submission/ScheduledIcon.svg";

import "./SubmittedSubmissionRow.css";

const EXPIRY_FLAG_ANIMATION = {
  initial: {
    bottom: 2,
    scale: 0.22,
    opacity: 1,
  },
  animate: {
    bottom: [2, 5, 5, 2],
    scale: [0.22, 1, 1, 0.22],
    opacity: [1, 1, 1, 0],
  },
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeOut",
    times: [0, 0.1, 0.82, 1],
  },
  style: {
    left: "50%",
    x: "-50%",
    transformOrigin: "50% 100%",
  },
};

export default function SubmittedSubmissionRow(props) {
  const {
    submission,
    handleClickSubmission,
    handleDeleteSubmission,
    handleAddSamples,
    hideActionsWhenDisabled,
    hideActions,
    search,
    searchField,
    showType,
    // typeLabel,
    submissionDetailsSearchWords,
    showSampleIssue = false,
    showExtendedColumns = false,
    showSubmissionType = false,
    onOpenActivityHistory,
    hasExpandColumn = false,
    isExpandable = false,
    isExpanded = false,
    onToggleExpand,
    onOpenSampleIssue,
    onOpenExpiryFlow,
    onEditScheduled,
    onDeleteScheduled,
    scheduledActionsLoading = false,
    showActionsColumn = false,
  } = props;
  const submissionModalHighlight = Array.isArray(submissionDetailsSearchWords)
    && submissionDetailsSearchWords.length > 0;
  const renderSubmissionModalHl = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    if (!submissionModalHighlight || !text) {
      return text;
    }
    return (
      <Highlighter
        highlightClassName="searchHighlight"
        searchWords={submissionDetailsSearchWords}
        autoEscape
        textToHighlight={text}
      />
    );
  };

  const hasRowSearchValue = Boolean(search && String(search).trim().length);
  const renderRowSearchHl = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    if (submissionModalHighlight
      || !hasRowSearchValue
      || searchField !== "all"
      || !text) {
      return text;
    }
    return (
      <Highlighter
        highlightClassName="PinDetailBlock__SearchHighlight"
        searchWords={[search]}
        autoEscape
        textToHighlight={text}
      />
    );
  };
  // const statusCodeToName = {
  //   "-1": "Submitted",
  //   0: "Samples Received",
  //   1: "Testing in Progress",
  //   2: "Testing Complete",
  // };
  const statusToColorClassName = {
    "-1": "submitted-submissions-status-text-submitted",
    0: "submitted-submissions-status-text-samples-received",
    1: "submitted-submissions-status-text-progress",
    2: "submitted-submissions-status-text-complete",
  };
  // const productSpecToColorClassName = {
  //   Added: "submitted-submissions-product-spec-color-added",
  //   Pending: "submitted-submissions-product-spec-color-incomplete",
  // };
  // const productSpecCodeToName = {
  //   Added: "Spec Added",
  //   Pending: "Spec Incomplete",
  // };
  const showStandardActionMenu = !hideActionsWhenDisabled && !hideActions;
  const eventTypeStyle = { textAlign: "center" };
  const showTrackingInfo = hasSubmissionTrackingInfo(submission);
  const showExpiry = hasSubmissionExpiry(submission) && !showTrackingInfo;
  const expiryDisplay = getSubmissionExpiryDisplay(submission);
  const showIssues = hasSampleIssues(submission);
  const isMultiIssue = isExpandableSampleIssues(submission);
  const showAllIssuesTooltip = allSamplesHaveIssues(submission) && isMultiIssue;
  const issueDisplay = getSampleIssueColumnDisplay(submission);
  const statusLabel = getSubmissionStatusLabel(submission);
  const statusClassName = getSubmissionStatusClassName(submission);
  const showScheduled = isScheduledSubmission(submission);
  const scheduledTooltip = showScheduled ? getScheduledSubmissionTooltip(submission) : "";
  const showDeleted = isDeletedSubmission(submission);
  const showScheduledActionMenu = showScheduled && showActionsColumn
    && typeof onEditScheduled === "function"
    && typeof onDeleteScheduled === "function";
  const isRetestSubmission = submissionHasRetestSample(submission);

  const renderStatusCategoryIcons = () => {
    if (showScheduled) {
      return (
        <CalendarOutlined
          className="submitted-submissions-status-calendar-icon"
          aria-hidden
        />
      );
    }

    if (showDeleted) {
      return (
        <DeleteByIcon
          className="submitted-submissions-status-deleted-icon"
          aria-hidden
        />
      );
    }

    const iconClassName = `${statusToColorClassName[submission.status]} submitted-submissions-status-icons`;
    const isProductAndEnvironment = submission.sample_category === PRODUCT_ENVIRONMENT_CATEGORY;
    const isProduct = submission.sample_category === "product";
    const isEnvironment = submission.sample_category === "environment";

    return (
      <span className="submitted-submissions-status-icon-slots">
        {isProductAndEnvironment && (
          <ProductEnvironmentIcon className={iconClassName} aria-label="Product and environment submission" />
        )}
        {isProduct && <ProductIcon className={iconClassName} />}
        {isEnvironment && <EnvironmentIcon className={iconClassName} />}
      </span>
    );
  };

  const renderStatusCellContent = () => (
    <div className={`submitted-submissions-status-content ${showScheduled ? "submitted-submissions-status-content--scheduled" : ""}`}>
      {renderStatusCategoryIcons()}
      <span className={statusClassName}>
        {submissionModalHighlight
          ? renderSubmissionModalHl(statusLabel)
          : renderRowSearchHl(statusLabel)}
      </span>
    </div>
  );

  const renderSubmissionNameTdContent = () => {
    if (submissionModalHighlight) {
      return renderSubmissionModalHl(submission.submit_name ?? "");
    }
    if (hasRowSearchValue && (searchField === "submission_name" || searchField === "all")) {
      return (
        <Highlighter
          highlightClassName="PinDetailBlock__SearchHighlight"
          searchWords={[search]}
          autoEscape
          textToHighlight={submission.submit_name ?? ""}
        />
      );
    }
    return submission.submit_name ?? "";
  };

  const renderSubmissionPoTdContent = () => {
    if (submissionModalHighlight) {
      return renderSubmissionModalHl(submission.po || "-");
    }
    if (hasRowSearchValue && (searchField === "po" || searchField === "all")) {
      return (
        <Highlighter
          highlightClassName="PinDetailBlock__SearchHighlight"
          searchWords={[search]}
          autoEscape
          textToHighlight={submission.po ?? ""}
        />
      );
    }
    return submission.po || "-";
  };

  const renderSampleIssueContent = () => {
    if (showTrackingInfo) {
      const trackingRows = getTrackingInfoTooltipRows(submission);
      const tooltipContent = (
        <div className="submitted-submissions-sample-issue__tooltip">
          <p className="submitted-submissions-sample-issue__tooltipBody">{TRACKING_INFO_TOOLTIP}</p>
          {trackingRows.length > 0 && (
            <div className="submitted-submissions-sample-issue__tooltipDetails">
              {trackingRows.map(({ label, value }) => (
                <p key={`${label}-${value}`} className="submitted-submissions-sample-issue__tooltipRow">
                  <span className="submitted-submissions-sample-issue__tooltipKey">
                    {label}
                    :
                  </span>
                  <span className="submitted-submissions-sample-issue__tooltipValue">{value}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      );

      return (
        <StyledTooltip
          title={tooltipContent}
          placement="top"
          className="SubmittedSubmissionRow__ExpiryTooltip"
        >
          <div className="submitted-submissions-sample-issue submitted-submissions-sample-issue--action-taken">
            <ActionTaken aria-hidden />
            <span className="submitted-submissions-sample-issue__actionTaken">Action Taken</span>
          </div>
        </StyledTooltip>
      );
    }

    if (showExpiry && expiryDisplay) {
      const tooltipContent = (
        <div className="submitted-submissions-sample-issue__tooltip">
          <p className="submitted-submissions-sample-issue__tooltipTitle">{expiryDisplay.tooltipTitle}</p>
          <p className="submitted-submissions-sample-issue__tooltipBody">{expiryDisplay.tooltipBody}</p>
        </div>
      );

      return (
        <StyledTooltip
          title={tooltipContent}
          placement="top"
          className="SubmittedSubmissionRow__ExpiryTooltip"
        >
          <div className="submitted-submissions-sample-issue submitted-submissions-sample-issue--expiry">
            <ExpiryTimer className="submitted-submissions-sample-issue__icon" aria-hidden />
            <div className="submitted-submissions-sample-issue__text">
              <span className="submitted-submissions-sample-issue__desc">{expiryDisplay.desc}</span>
              {expiryDisplay.date && (
                <span className="submitted-submissions-sample-issue__date">{expiryDisplay.date}</span>
              )}
            </div>
          </div>
        </StyledTooltip>
      );
    }

    if (issueDisplay) {
      if (issueDisplay.type === "count") {
        return <SampleIssuePill count={issueDisplay.count} />;
      }
      return (
        <SampleIssuePill
          issueType={issueDisplay.issueType}
          label={issueDisplay.label}
          showCreditsRefunded={issueDisplay.showCreditsRefunded}
        />
      );
    }

    const sampleIssueValue = submission?.sample_issue;
    if (sampleIssueValue != null && String(sampleIssueValue).trim() !== "") {
      return String(sampleIssueValue);
    }

    return "N/A";
  };

  const rowClickHandler = () => {
    if (showAllIssuesTooltip) {
      return;
    }
    if (showIssues && !isMultiIssue && typeof onOpenSampleIssue === "function") {
      onOpenSampleIssue(submission);
      return;
    }
    if (handleClickSubmission) {
      handleClickSubmission(submission);
    }
  };

  const wrapRowClickContent = (content) => {
    if (!showAllIssuesTooltip) return content;
    return (
      <StyledTooltip title={ALL_SAMPLES_HAVE_ISSUES_TOOLTIP} placement="top">
        <span className="submitted-submission-row__all-issues">{content}</span>
      </StyledTooltip>
    );
  };

  const sampleIssueClickHandler = () => {
    if (showExpiry && typeof onOpenExpiryFlow === "function") {
      onOpenExpiryFlow(submission);
      return;
    }
    if (isMultiIssue && typeof onToggleExpand === "function") {
      onToggleExpand(submission);
      return;
    }
    rowClickHandler();
  };

  const handleExpandClick = (event) => {
    event.stopPropagation();
    if (typeof onToggleExpand === "function") {
      onToggleExpand(submission);
    }
  };

  // const renderDateTimeCell = (dateValue) => {
  //   const dateText = formatSubmissionDateTime(dateValue);
  //   if (!dateText) return "";
  //   return submissionModalHighlight
  //     ? renderSubmissionModalHl(dateText)
  //     : renderRowSearchHl(dateText);
  // };

  const handleLastActionClick = (event) => {
    event.stopPropagation();
    if (typeof onOpenActivityHistory === "function") {
      onOpenActivityHistory(submission);
    }
  };

  const lastActionOnText = formatSubmissionDateTime(submission.last_action_on);
  const lastActionByText = submission.last_action_by || "-";
  const hasActivityHistory = getActivityHistoryItems(submission).length > 0;
  const totalTestsText = getTotalTestsDisplay(submission);

  return (
    <tr className={`submitted-submission-row ${showIssues ? "submitted-submission-row--has-issues" : ""}`}>
      {hasExpandColumn && (
        <td className="submitted-submissions-expand-td">
          {isExpandable ? (
            <button
              type="button"
              className={`submitted-submissions-expand-btn ${isExpanded ? "submitted-submissions-expand-btn--expanded" : ""}`}
              aria-label={isExpanded ? "Collapse issue samples" : "Expand issue samples"}
              onClick={handleExpandClick}
            >
              <ChevronRight aria-hidden />
            </button>
          ) : showExpiry ? (
            <div
              className="submitted-submissions-expiry-flag-cell"
              aria-label="Submission expiry warning"
            >
              <motion.div
                className="submitted-submissions-expiry-flag"
                {...EXPIRY_FLAG_ANIMATION}
              >
                <ExpiryFlag aria-hidden />
              </motion.div>
            </div>
          ) : showScheduled ? (
            <StyledTooltip title={scheduledTooltip} placement="top">
              <ScheduledIcon
                className="submitted-submissions-scheduled-clock"
                aria-label="Scheduled submission"
              />
            </StyledTooltip>
          ) : (
            <span className="submitted-submissions-expand-placeholder" aria-hidden />
          )}
        </td>
      )}
      <td
        onClick={rowClickHandler}
        className="submitted-submissions-status-td"
      >
        {statusLabel && (
          showAllIssuesTooltip
            ? wrapRowClickContent(renderStatusCellContent())
            : (
              showScheduled && scheduledTooltip ? (
                <StyledTooltip title={scheduledTooltip} placement="top">
                  {renderStatusCellContent()}
                </StyledTooltip>
              ) : renderStatusCellContent()
            )
        )}
      </td>
      <td onClick={rowClickHandler}>
        <div title={submission.submit_name} className="submitted-submission-name">
          {wrapRowClickContent(renderSubmissionNameTdContent())}
        </div>
      </td>
      <td onClick={rowClickHandler} className="submitted-submissions-po-td">
        {wrapRowClickContent(renderSubmissionPoTdContent())}
      </td>
      {showSubmissionType && (
        <td onClick={rowClickHandler} className="submitted-submissions-type-td">
          {wrapRowClickContent(<SubmissionTypeLabel submission={submission} />)}
        </td>
      )}
      {showExtendedColumns && (
        <td className="submitted-submissions-last-action-on-td">
          {lastActionOnText && hasActivityHistory ? (
            <button
              type="button"
              className="submitted-submissions-last-action-link"
              onClick={handleLastActionClick}
            >
              {submissionModalHighlight
                ? renderSubmissionModalHl(lastActionOnText)
                : renderRowSearchHl(lastActionOnText)}
            </button>
          ) : (
            <span className="submitted-submissions-last-action-text">
              {lastActionOnText
                ? (submissionModalHighlight
                  ? renderSubmissionModalHl(lastActionOnText)
                  : renderRowSearchHl(lastActionOnText))
                : "-"}
            </span>
          )}
        </td>
      )}
      {showExtendedColumns && (
        <td onClick={rowClickHandler} className="submitted-submissions-last-action-by-td">
          {wrapRowClickContent(
            submissionModalHighlight
              ? renderSubmissionModalHl(lastActionByText)
              : renderRowSearchHl(lastActionByText),
          )}
        </td>
      )}
      {/* <td onClick={rowClickHandler} className="submitted-submissions-submitted-on-td">
        <div>
          {submission.submit_date ? renderDateTimeCell(submission.submit_date) : ""}
        </div>
      </td>
      <td onClick={rowClickHandler} className="submitted-submissions-submitted-by-td">
        {(() => {
          if (submissionModalHighlight) {
            return renderSubmissionModalHl(submission.submitted_by || "-");
          }
          return submission.submitted_by ? renderRowSearchHl(submission.submitted_by) : "-";
        })()}
      </td> */}
      <td onClick={rowClickHandler} className="submitted-submissions-table-samples-number-td">
        {wrapRowClickContent(
          submissionModalHighlight
            ? renderSubmissionModalHl(String(submission.num_samples ?? ""))
            : renderRowSearchHl(String(submission.num_samples ?? "")),
        )}
      </td>
      {showExtendedColumns && (
        <td onClick={rowClickHandler} className="submitted-submissions-table-total-tests-td">
          {wrapRowClickContent(
            submissionModalHighlight
              ? renderSubmissionModalHl(totalTestsText)
              : renderRowSearchHl(totalTestsText),
          )}
        </td>
      )}
      {showSampleIssue && (
        <td onClick={sampleIssueClickHandler} className="submitted-submissions-sample-issue-td">
          {renderSampleIssueContent()}
        </td>
      )}
      {/* <td
        onClick={
          handleClickSubmission
            ? () => handleClickSubmission(submission)
            : () => { }
        }
        className="submitted-submissions-product-spec-td"
      >
        <div>
          <span
            className={`submitted-submissions-product-spec-color ${productSpecToColorClassName[submission.spec_status]}`}
          />
          <span className="submitted-submissions-product-spec-text">
            {submissionModalHighlight
              ? renderSubmissionModalHl(productSpecCodeToName[submission.spec_status] ?? "")
              : renderRowSearchHl(productSpecCodeToName[submission.spec_status] ?? "")}
          </span>
        </div>
      </td> */}
      {showStandardActionMenu && (
        <td className="submitted-submissions-table-actions-td">
          <ActionsMenu
            disabled={hideActionsWhenDisabled}
            handleDelete={() => handleDeleteSubmission(submission)}
            handleAdd={() => handleAddSamples(submission)}
            disableAddRetest={isRetestSubmission}
            disabledTooltipText="Cannot delete or add to submission at this stage"
          />
        </td>
      )}
      {showScheduledActionMenu && (
        <td
          className="submitted-submissions-table-actions-td"
          onClick={(event) => event.stopPropagation()}
        >
          <ActionsMenu
            handleEdit={() => onEditScheduled(submission)}
            disableEditRetestSubmission={isRetestSubmission}
            handleDelete={() => onDeleteScheduled(submission)}
            disabled={scheduledActionsLoading}
            disabledTooltipText="Loading scheduled submission…"
          />
        </td>
      )}
      {!showStandardActionMenu && showActionsColumn && !showScheduledActionMenu && (
        <td className="submitted-submissions-table-actions-td" aria-hidden />
      )}
      {showType && (<td style={eventTypeStyle}>Event</td>)}
    </tr>
  );
}
